import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router';
import { Loading } from '@framework/ui/atoms';
import { useDetectGroupId, useGroupEntity } from '@group/hooks';
import { SidebarTree, useSidebarTree } from './SidebarTree';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { faBuilding, faObjectGroup } from '@fortawesome/free-regular-svg-icons';
import type { SidebarTabTarget } from './SidebarTabContainer';
import { SidebarTabContainer } from './SidebarTabContainer';
import { Timestamp } from '@framework/Timestamp';
import { CookieUtils } from '@framework/CookieUtils';
import { SidebarSearchPanel } from './SidebarSearch';
import { useAtomValue, useSetAtom } from 'jotai';
import { controlAtom, sidebarTreeContainerRefAtom } from './SidebarTree/SidebarTreeStore/sidebarTreeControlState';
import { SidebarBookmarks } from './SidebarBookmarks';
import { isDebugMode } from '@framework/utils';
import { trpcClient } from '@framework/trpcClient';
import { useGroupMyMemberRole } from '@group/hooks/useGroupMyMemberRole';
import { GetFunctionsClient } from '@framework/firebase';

type Props = {
  handleSidebarOpen: () => void;
};

const SidebarTargetCookieName = 'balus_sidebar';

const setSidebarTargetToCookie = (target: SidebarTabTarget) => {
  const now = Timestamp.now();
  const expires = now.addDays(365).toDate();
  CookieUtils.set(SidebarTargetCookieName, JSON.stringify(target), { expires });
};

const getSidebarTargetFromCookie = (): SidebarTabTarget => {
  const cookieValue = CookieUtils.get(SidebarTargetCookieName);

  if (!cookieValue) return 'Shared';

  return JSON.parse(cookieValue) || 'Shared';
};

export const SidebarContainer: React.FC<Props> = ({ handleSidebarOpen }: Props) => {
  const { t } = useTranslation();
  const sidebarTreeContainerRef = useRef<HTMLDivElement>(null);
  const setSidebarTreeContainerRef = useSetAtom(sidebarTreeContainerRefAtom);
  const targetGroupId = useDetectGroupId();
  const [sidebarTabTarget, setSidebarTabTarget] = useState<SidebarTabTarget>(getSidebarTargetFromCookie());
  const { firstLoaded } = useSidebarTree(targetGroupId);
  const { loading: groupLoading, group } = useGroupEntity(targetGroupId);
  const isGroupAdminUser = useGroupMyMemberRole(targetGroupId) === 'admin';
  const { scrolledViewModelId } = useAtomValue(controlAtom);

  const [isSearchPanelVisible, setIsSearchPanelVisible] = useState(false);

  const testErrorIsEnabled = isDebugMode('test-error');

  // refをatomに設定
  useEffect(() => {
    setSidebarTreeContainerRef(sidebarTreeContainerRef);
  }, [setSidebarTreeContainerRef]);

  const handleShowShared = useCallback(() => {
    setSidebarTabTarget('Shared');
    setSidebarTargetToCookie('Shared');
  }, []);
  const handleShowPersonal = useCallback(() => {
    setSidebarTabTarget('Personal');
    setSidebarTargetToCookie('Personal');
  }, []);
  const handleShowBookmarks = useCallback(() => {
    setSidebarTabTarget('Bookmark');
    setSidebarTargetToCookie('Bookmark');
  }, []);

  const handleSearchPanelOpen = useCallback(() => {
    setIsSearchPanelVisible(true);
  }, []);

  const handleSearchPanelClose = useCallback(() => {
    setIsSearchPanelVisible(false);
  }, []);

  if (groupLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (group === null) {
    return <div className="m-4 flex justify-center">{t('sidebar.container.groupNotFound')}</div>;
  }

  return (
    <div className="relative">
      {testErrorIsEnabled && (
        <div className="flex flex-col gap-2">
          <button
            className="border border-gray-300 p-1 active:bg-gray-100"
            onClick={() => {
              alert('例外を発生させます');
              throw new Error('test error webapp');
            }}
          >
            throw exception
          </button>
          <button
            className="border border-gray-300 p-1 active:bg-gray-100"
            onClick={() => {
              alert('functionsで例外を発生させます');
              GetFunctionsClient().httpsCallable('hello')({ action: 'error' });
            }}
          >
            throw exception in functions
          </button>
          <button
            className="border border-gray-300 p-1 active:bg-gray-100"
            onClick={() => {
              alert('tRPCで例外を発生させます');
              trpcClient.testError.query();
            }}
          >
            throw exception in tRPC
          </button>
        </div>
      )}
      <div className="pb-1">
        <div className="ml-4 pb-2 pt-6">
          <div className="mt-1 flex justify-between">
            <Link
              to={UserPagePathBuilder.groupPage(group.id)}
              className="flex max-w-lg items-center truncate text-lg font-bold text-black hover:text-blue-900"
            >
              <span className="mr-1">
                {group.imageUrl ? (
                  <span className="inline-block size-8 overflow-hidden rounded-full align-middle">
                    <img className="size-full object-cover" src={group.imageUrl} alt={group.name.toString()} />
                  </span>
                ) : (
                  <FontAwesomeIcon icon={faBuilding} className="text-brand" />
                )}
              </span>
              {group.name.toString()}
            </Link>
            <button
              className="mx-4 focus:outline-none"
              onClick={handleSidebarOpen}
              aria-label={t('sidebar.container.closeSidebar')}
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </button>
          </div>
          <div className="mt-4 rounded-sm py-1 hover:bg-gray-300">
            <a
              href={UserPagePathBuilder.kataMapPage('sidebar')}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-start"
            >
              <FontAwesomeIcon icon={faObjectGroup} className="mr-1 text-brand" />
              <span>KATA MAP</span>
            </a>
          </div>
          <div className="my-2">
            <button
              className="flex w-full items-center rounded-sm py-1 pr-2 hover:bg-gray-300 focus:outline-none"
              onClick={handleSearchPanelOpen}
            >
              <FontAwesomeIcon icon={faSearch} className="mr-1 text-brand" />
              <span>{t('common.action.search')}</span>
            </button>
          </div>
        </div>
        <div>
          <SidebarTabContainer
            target={sidebarTabTarget}
            handleShowShared={handleShowShared}
            handleShowPersonal={handleShowPersonal}
            handleShowBookmarks={handleShowBookmarks}
          />
        </div>
      </div>
      <div className="ml-4">
        {/* フォルダツリー */}
        {/* 165pxはサイドバーのスクロールしない部分の高さ。本当はrefを使って高さを取得したいが、レンダリング時点でref.currentがundefinedになるので直接指定している */}
        <div
          ref={sidebarTreeContainerRef}
          className="overflow-y-auto overflow-x-hidden text-sm"
          style={{ height: 'calc(100vh - 210px)' }}
        >
          {/* 検索結果 > ブックマーク > 通常(ワークスペース) の優先度でサイドバーは表示される */}
          {/* 検索結果と通常(ワークスペース)はどちらもSidebarTreeを使って表示される */}
          {!firstLoaded ? (
            <Loading />
          ) : (
            <>
              <SidebarBookmarks target={sidebarTabTarget} scrolledViewModelId={scrolledViewModelId} />
              <SidebarTree target={sidebarTabTarget} groupId={targetGroupId} isGroupAdminUser={isGroupAdminUser} />
            </>
          )}
          <div className="m-8" />
        </div>
      </div>

      <SidebarSearchPanel isVisible={isSearchPanelVisible} onClose={handleSearchPanelClose} groupId={targetGroupId} />
    </div>
  );
};
